<template>
  <div class="expenses-shell">
    <!-- HERO -->
    <section class="expenses-hero">
      <div>
        <p class="eyebrow">LotLedger Wine Bar</p>
        <h1>Expenses</h1>
        <p class="hero-copy">
          Track supplies, repairs, licenses, tasting room costs, and all the little things that keep the lights low and the pours flowing.
        </p>
      </div>

      <button @click="openAddPopup" class="primary-action">
        + Add Expense
      </button>
    </section>

    <!-- SUMMARY -->
    <section class="expense-summary-grid">
      <article class="summary-card">
        <span>Total Showing</span>
        <strong>${{ money(filteredTotal) }}</strong>
      </article>

      <article class="summary-card">
        <span>Expense Count</span>
        <strong>{{ sortedExpenses.length }}</strong>
      </article>

      <article class="summary-card">
        <span>Average Expense</span>
        <strong>${{ money(averageExpense) }}</strong>
      </article>
    </section>

    <!-- FILTERS -->
    <section class="expense-toolbar">
      <div class="search-wrap">
        <span class="search-icon">⌕</span>
        <input
          type="text"
          v-model="filters.description"
          placeholder="Search description..."
        />
      </div>

      <div class="search-wrap">
        <span class="search-icon">↦</span>
        <input
          type="date"
          v-model="filters.startDate"
        />
      </div>

      <div class="search-wrap">
        <span class="search-icon">↤</span>
        <input
          type="date"
          v-model="filters.endDate"
        />
      </div>

      <button type="button" @click="clearFilters" class="ghost-action">
        Clear
      </button>
    </section>

    <!-- EXPENSE CARDS -->
    <section v-if="sortedExpenses.length > 0" class="expense-grid">
      <article
        v-for="expense in sortedExpenses"
        :key="expense?.id"
        class="expense-card"
      >
        <div class="expense-main">
          <div class="expense-title-row">
            <div>
              <p class="label">Expense #{{ expense.id }}</p>
              <h2>{{ expense.description }}</h2>
            </div>

            <span class="status-pill">
              ${{ money(expense.cost) }}
            </span>
          </div>

          <div class="expense-details">
            <div>
              <span>Category</span>
              <strong>{{ expense.category_name || '—' }}</strong>
            </div>

            <div>
              <span>Purchase Date</span>
              <strong>{{ formatDate(expense.purchase_date) }}</strong>
            </div>
          </div>
        </div>

        <div class="card-actions">
          <button @click="openEditPopup(expense)" class="ghost-action">
            Edit
          </button>

          <button @click="confirmDelete(expense)" class="danger-action">
            Delete
          </button>
        </div>
      </article>
    </section>

    <!-- EMPTY STATE -->
    <section v-else class="empty-state">
      <p class="eyebrow">No Expenses</p>
      <h2>No matching expenses found.</h2>
      <p>Add your first expense or adjust the filters.</p>

      <button @click="openAddPopup" class="primary-action">
        + Add Expense
      </button>
    </section>

    <!-- ADD / EDIT EXPENSE MODAL -->
    <div v-if="showPopup" class="modal-overlay">
      <div class="expense-modal">
        <div class="modal-topline"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">
              {{ editing ? 'Edit Cost' : 'New Cost' }}
            </p>
            <h2>{{ editing ? 'Edit Expense' : 'Add Expense' }}</h2>
            <p>
              Record operational costs that should flow into reports and closeout review.
            </p>
          </div>

          <button type="button" @click="showPopup = false" class="icon-close">
            ×
          </button>
        </header>

        <form @submit.prevent="submitExpense" class="modal-body">
          <section class="form-panel">
            <div class="panel-heading">
              <h3>Expense Details</h3>
              <p>Keep it simple, but categorize it cleanly.</p>
            </div>

            <div class="form-grid">
              <label>
                <span>Description</span>
                <input
                  type="text"
                  v-model="currentExpense.description"
                  required
                  placeholder="Example: Cheese order, candles, glassware..."
                />
              </label>

              <label>
                <span>Category</span>
                <select v-model="currentExpense.category_id" required>
                  <option value="" disabled>Select a category</option>
                  <option
                    v-for="category in categories"
                    :key="category.category_id"
                    :value="category.category_id"
                  >
                    {{ category.category_name }}
                  </option>
                </select>
              </label>

              <label>
                <span>Purchase Date</span>
                <input
                  type="date"
                  v-model="currentExpense.purchase_date"
                  required
                />
              </label>

              <label>
                <span>Cost</span>
                <input
                  type="number"
                  v-model.number="currentExpense.cost"
                  min="0"
                  step="0.01"
                  required
                  placeholder="0.00"
                />
              </label>
            </div>
          </section>

          <footer class="modal-actions">
            <button type="button" @click="showPopup = false" class="cancel-action">
              Cancel
            </button>

            <button type="submit" class="primary-action">
              {{ editing ? 'Update Expense' : 'Save Expense' }}
            </button>
          </footer>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'ExpensePage',

  data() {
    return {
      expenses: [],
      categories: [],
      showPopup: false,
      editing: false,

      filters: {
        description: '',
        startDate: '',
        endDate: ''
      },

      currentExpense: this.blankExpense()
    };
  },

  computed: {
    sortedExpenses() {
      return this.filteredExpenses
        .slice()
        .sort((a, b) => new Date(b.purchase_date) - new Date(a.purchase_date));
    },

    filteredExpenses() {
      return this.expenses.filter(expense => {
        const description = String(expense.description || '').toLowerCase();
        const purchaseDate = this.normalizeDate(expense.purchase_date);

        const passesDescription =
          !this.filters.description ||
          description.includes(this.filters.description.toLowerCase());

        const passesStartDate =
          !this.filters.startDate ||
          purchaseDate >= this.filters.startDate;

        const passesEndDate =
          !this.filters.endDate ||
          purchaseDate <= this.filters.endDate;

        return passesDescription && passesStartDate && passesEndDate;
      });
    },

    filteredTotal() {
      return this.sortedExpenses.reduce((sum, expense) => {
        return sum + Number(expense.cost || 0);
      }, 0);
    },

    averageExpense() {
      if (!this.sortedExpenses.length) {
        return 0;
      }

      return this.filteredTotal / this.sortedExpenses.length;
    }
  },

  methods: {
    blankExpense() {
      return {
        id: null,
        description: '',
        category_id: '',
        purchase_date: new Date().toISOString().split('T')[0],
        cost: null
      };
    },

    async fetchExpenses() {
      try {
        const response = await api.get('/expenses');

        this.expenses = (response.data || []).map(expense => ({
          ...expense,
          cost: Number(expense.cost || 0)
        }));
      } catch (error) {
        console.error('❌ Error fetching expenses:', error);
      }
    },

    async fetchCategories() {
      try {
        const response = await api.get('/expense-categories');
        this.categories = response.data || [];
      } catch (error) {
        console.error('❌ Error fetching expense categories:', error);
      }
    },

    openAddPopup() {
      this.currentExpense = this.blankExpense();
      this.editing = false;
      this.showPopup = true;
    },

    openEditPopup(expense) {
      this.currentExpense = {
        ...expense,
        purchase_date: this.normalizeDate(expense.purchase_date),
        cost: Number(expense.cost || 0)
      };

      this.editing = true;
      this.showPopup = true;
    },

    async submitExpense() {
      try {
        const payload = {
          ...this.currentExpense,
          cost: Number(this.currentExpense.cost || 0)
        };

        if (this.editing) {
          await api.put(`/expenses/${this.currentExpense.id}`, payload);
        } else {
          await api.post('/expenses', payload);
        }

        this.showPopup = false;
        await this.fetchExpenses();
      } catch (error) {
        console.error('❌ Error saving expense:', error);
        alert('Failed to save expense. Please check the logs.');
      }
    },

    async confirmDelete(expense) {
      if (!confirm(`Delete "${expense.description}"?`)) {
        return;
      }

      try {
        await api.delete(`/expenses/${expense.id}`);
        await this.fetchExpenses();
      } catch (error) {
        console.error('❌ Error deleting expense:', error);
        alert('Failed to delete expense.');
      }
    },

    clearFilters() {
      this.filters = {
        description: '',
        startDate: '',
        endDate: ''
      };
    },

    normalizeDate(date) {
      if (!date) {
        return '';
      }

      return new Date(date).toISOString().split('T')[0];
    },

    formatDate(date) {
      if (!date) {
        return '—';
      }

      return new Date(date).toLocaleDateString('en-US');
    },

    money(value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(value || 0));
    }
  },

  mounted() {
    this.fetchExpenses();
    this.fetchCategories();
  }
};
</script>

<style scoped>
.expenses-shell {
  min-height: 100vh;
  padding: 28px;
  color: #f7efe8;
  font-family: "Inter", "Poppins", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.38), transparent 34%),
    radial-gradient(circle at bottom right, rgba(194, 74, 47, 0.18), transparent 32%),
    linear-gradient(135deg, #0d090d 0%, #171016 42%, #24141b 100%);
}

.expenses-hero,
.expense-toolbar,
.summary-card,
.expense-card,
.empty-state,
.expense-modal {
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.085), rgba(255, 255, 255, 0.035));
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.34);
  backdrop-filter: blur(18px);
}

.expenses-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 22px;
  padding: 30px;
}

.eyebrow,
.label {
  margin: 0 0 8px;
  color: #c8a66a;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 12px;
  font-weight: 900;
}

.label {
  color: #9b8f8b;
  letter-spacing: 0.12em;
}

.expenses-hero h1 {
  margin: 0;
  font-size: clamp(42px, 7vw, 78px);
  line-height: 0.92;
  letter-spacing: -0.07em;
}

.hero-copy {
  max-width: 660px;
  margin: 16px 0 0;
  color: #cfc2bd;
  font-size: 16px;
  line-height: 1.5;
}

.expense-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(180px, 1fr));
  gap: 14px;
  margin-top: 18px;
}

.summary-card {
  padding: 20px;
}

.summary-card span {
  display: block;
  color: #a99d98;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.summary-card strong {
  display: block;
  margin-top: 10px;
  font-size: 34px;
  line-height: 1;
  letter-spacing: -0.05em;
}

.expense-toolbar {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 180px 180px auto;
  gap: 12px;
  align-items: center;
  margin-top: 18px;
  padding: 18px;
}

.search-wrap {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #c8a66a;
  font-weight: 900;
  pointer-events: none;
}

.search-wrap input {
  padding-left: 38px;
}

.expense-grid {
  display: grid;
  gap: 14px;
  margin-top: 18px;
}

.expense-card {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 18px;
  padding: 20px;
}

.expense-title-row {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.expense-title-row h2 {
  margin: 0;
  font-size: clamp(22px, 3vw, 36px);
  line-height: 0.98;
  letter-spacing: -0.055em;
}

.status-pill {
  display: inline-flex;
  min-height: 38px;
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  border-radius: 999px;
  color: #f1c979;
  font-size: 14px;
  font-weight: 900;
  white-space: nowrap;
  background: rgba(200, 166, 106, 0.16);
  border: 1px solid rgba(200, 166, 106, 0.28);
}

.expense-details {
  display: grid;
  grid-template-columns: repeat(2, minmax(160px, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.expense-details div {
  padding: 14px;
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.065);
}

.expense-details span {
  display: block;
  margin-bottom: 6px;
  color: #9b8f8b;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.expense-details strong {
  color: #fff8f1;
  font-size: 15px;
}

.card-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 120px;
}

.empty-state {
  margin-top: 18px;
  padding: 46px 30px;
  text-align: center;
}

.empty-state h2 {
  margin: 0;
  font-size: clamp(32px, 5vw, 54px);
  letter-spacing: -0.06em;
}

.empty-state p {
  color: #cfc2bd;
}

.expenses-shell input,
.expenses-shell select {
  min-height: 52px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  outline: 0;
  background: rgba(6, 4, 6, 0.58);
  color: #fff8f1;
  padding: 0 14px;
  font-size: 15px;
  font-family: inherit;
}

.expenses-shell input::placeholder {
  color: #766c68;
}

.expenses-shell input:focus,
.expenses-shell select:focus {
  border-color: rgba(200, 166, 106, 0.65);
  box-shadow: 0 0 0 3px rgba(200, 166, 106, 0.14);
}

.primary-action,
.ghost-action,
.cancel-action,
.danger-action {
  min-height: 50px;
  padding: 0 18px;
  border-radius: 999px;
  border: 0;
  color: #fff8f1;
  font-weight: 900;
  font-size: 14px;
  cursor: pointer;
  transition: transform 0.14s ease, box-shadow 0.14s ease, background 0.14s ease;
}

.primary-action {
  background: linear-gradient(135deg, #8f123f, #c24a2f);
  box-shadow: 0 14px 32px rgba(194, 74, 47, 0.3);
}

.ghost-action,
.cancel-action {
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.11);
}

.danger-action {
  background: rgba(155, 45, 45, 0.38);
  border: 1px solid rgba(255, 118, 118, 0.25);
  color: #ffb4a8;
}

.primary-action:hover,
.ghost-action:hover,
.cancel-action:hover,
.danger-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.24);
}

/* ---------- Modal ---------- */

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 26px;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.34), transparent 36%),
    rgba(5, 3, 5, 0.78);
  backdrop-filter: blur(12px);
}

.expense-modal {
  width: min(760px, 94vw);
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background:
    linear-gradient(145deg, rgba(35, 22, 29, 0.98), rgba(14, 10, 14, 0.98));
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.55);
}

.modal-topline {
  height: 5px;
  background: linear-gradient(90deg, #8f123f, #c24a2f, #c8a66a);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  padding: 26px 28px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.modal-header h2 {
  margin: 0;
  font-size: 40px;
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.modal-header p {
  margin: 10px 0 0;
  color: #cfc2bd;
  line-height: 1.45;
}

.icon-close {
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.07);
  color: #fff8f1;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
}

.modal-body {
  padding: 24px 28px 28px;
  overflow-y: auto;
}

.form-panel {
  padding: 18px;
  border-radius: 24px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.075);
}

.panel-heading {
  margin-bottom: 16px;
}

.panel-heading h3 {
  margin: 0;
  font-size: 24px;
  letter-spacing: -0.04em;
}

.panel-heading p {
  margin: 6px 0 0;
  color: #a99d98;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 14px;
}

.expenses-shell label {
  display: grid;
  gap: 8px;
}

.expenses-shell label span {
  color: #b9aaa4;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 18px;
}

/* ---------- Responsive ---------- */

@media (max-width: 900px) {
  .expenses-hero,
  .expense-card {
    grid-template-columns: 1fr;
  }

  .expenses-hero {
    flex-direction: column;
    align-items: stretch;
  }

  .expense-summary-grid,
  .expense-toolbar,
  .expense-details,
  .form-grid {
    grid-template-columns: 1fr;
  }

  .card-actions {
    flex-direction: row;
    min-width: 0;
  }
}

@media (max-width: 600px) {
  .expenses-shell {
    padding: 16px;
  }

  .modal-overlay {
    padding: 12px;
    align-items: stretch;
  }

  .expense-modal {
    width: 100%;
    max-height: calc(100vh - 24px);
    border-radius: 24px;
  }

  .modal-header {
    flex-direction: column;
  }

  .modal-actions,
  .card-actions {
    flex-direction: column;
  }

  .primary-action,
  .ghost-action,
  .cancel-action,
  .danger-action {
    width: 100%;
  }
}
</style>